import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import apiClient from '../../../services/apiClient';

const InputField = React.memo(({
  label,
  name,
  type = "text",
  autoComplete,
  placeholder,
  value,
  onChange,
  error,
  children,
  isOptional = false
}) => (
  <div className="mb-4">
    {label && (
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {!isOptional && <span className="text-red-500">*</span>}
      </label>
    )}
    <div
      className={`bg-white p-3 rounded-lg shadow-md ring-1 mt-1 ${
        error ? 'ring-red-500' : 'ring-gray-100'
      }`}
    >
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-500"
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
      />
    </div>
    {children}
    {error && (
      <p id={`${name}-error`} className="mt-1 text-xs text-red-600">
        {error}
      </p>
    )}
  </div>
));

const StepThreeYourInfo = ({ onNext, onPrevious, formData }) => {
  const [contactInfo, setContactInfo] = useState(
    formData.stepThreeContactInfo || {
      fullName: '',
      phone: '',
      email: '',
      streetAddress: '',
      aptSuite: '',
      city: 'Houston',
      zipCode: ''
    }
  );
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleChange = e => {
    const { name, value } = e.target;
    setContactInfo(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!contactInfo.fullName.trim()) newErrors.fullName = 'Full name is required.';
    const phoneDigits = contactInfo.phone.replace(/\D/g, '');
    if (!phoneDigits) newErrors.phone = 'Phone number is required.';
    else if (phoneDigits.length !== 10) newErrors.phone = 'Phone number must be 10 digits.';
    if (!contactInfo.email.trim()) newErrors.email = 'Email address is required.';
    else if (!/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/.test(contactInfo.email))
      newErrors.email = 'Invalid email format. Example: user@example.com';
    if (!contactInfo.streetAddress.trim()) newErrors.streetAddress = 'Street address is required.';
    if (!contactInfo.city.trim()) newErrors.city = 'City is required.';
    if (!contactInfo.zipCode.trim()) newErrors.zipCode = 'ZIP code is required.';
    else if (!/^\d{5}$/.test(contactInfo.zipCode))
      newErrors.zipCode = 'ZIP code must be 5 digits.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validate()) {
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(true);

    const payload = {
      email: contactInfo.email,
      fullName: contactInfo.fullName,
      phone: contactInfo.phone.replace(/\D/g, ''),
      contactStreet: contactInfo.streetAddress,
      contactCity: contactInfo.city,
      contactZip: contactInfo.zipCode,
      contactApartment: contactInfo.aptSuite || '',
    };

    console.log('isAuthenticated:', isAuthenticated);
    console.log('Request Payload:', payload);

    let leadDataForNextStep = { stepThreeContactInfo: contactInfo };

    if (!isAuthenticated) {
      try {
        const result = await apiClient.post('/auth/register-service-lead', payload);
        
        console.log('API Response:', result.data);
        
        // Handle different response structures
        if (result.data?.user?.id) {
          leadDataForNextStep.guestUserId = result.data.user.id;
        } else if (result.data?.id) {
          leadDataForNextStep.guestUserId = result.data.id;
        }

        // Check for success in response
        if (result.data?.success === false) {
          throw new Error(result.data.message || 'Failed to create lead account.');
        }

      } catch (err) {
        console.error("Error registering service lead:", err);
        
        let errorMessage = 'Could not create a temporary account. Please try again.';
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setErrors(prev => ({ ...prev, form: errorMessage }));
        setIsSubmitting(false);
        return;
      }
    }

    onNext(leadDataForNextStep);
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-brand-text-dark">
          Your Contact & Service Location
        </h3>
        <p className="text-sm text-brand-text-light mt-1">
          Please provide your information so we can reach you about your service request.
        </p>
      </div>

      <fieldset className="p-6 border border-gray-200 rounded-lg bg-white shadow-lg ring-1 ring-gray-100">
        <legend className="text-lg font-semibold text-brand-green-medium px-2 mb-4">
          Contact Information
        </legend>
        {errors.form && <p className="mb-3 text-sm text-red-600 bg-red-50 p-3 rounded">{errors.form}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <InputField
            label="Full Name"
            name="fullName"
            value={contactInfo.fullName}
            onChange={handleChange}
            autoComplete="name"
            error={errors.fullName}
          />
          <InputField
            label="Phone Number"
            name="phone"
            type="tel"
            value={contactInfo.phone}
            onChange={handleChange}
            autoComplete="tel"
            placeholder="(555) 123-4567"
            error={errors.phone}
          >
            <p className="text-xs text-gray-500 mt-1">Enter a 10-digit phone number.</p>
          </InputField>
        </div>
        <InputField
          label="Email Address"
          name="email"
          type="email"
          value={contactInfo.email}
          onChange={handleChange}
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email}
        />
      </fieldset>

      <fieldset className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
        <legend className="text-lg font-semibold text-brand-green-medium px-2 mb-4">
          Service Address
        </legend>
        <InputField
          label="Street Address"
          name="streetAddress"
          value={contactInfo.streetAddress}
          onChange={handleChange}
          autoComplete="street-address"
          error={errors.streetAddress}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
          <div className="md:col-span-2">
            <InputField
              label="City"
              name="city"
              value={contactInfo.city}
              onChange={handleChange}
              autoComplete="address-level2"
              error={errors.city}
            />
          </div>
          <InputField
            label="ZIP Code"
            name="zipCode"
            value={contactInfo.zipCode}
            onChange={handleChange}
            autoComplete="postal-code"
            pattern="[0-9]*"
            inputMode="numeric"
            maxLength="5"
            error={errors.zipCode}
          />
        </div>
        <InputField
          label="Apartment, suite, unit, etc."
          name="aptSuite"
          value={contactInfo.aptSuite}
          onChange={handleChange}
          placeholder="e.g., Apt 10B"
          isOptional
          error={errors.aptSuite}
        />
      </fieldset>

      <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200">
        <button type="button" onClick={onPrevious} className="cta-button-secondary" disabled={isSubmitting}>
          Previous
        </button>
        <button type="button" onClick={handleNext} className="cta-button" disabled={isSubmitting}>
          {isSubmitting ? 'Processing...' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default StepThreeYourInfo;